import { Fragment, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import {
  listWaivers,
  signWaiver,
  type SignerRelationship,
  type Waiver,
  type WaiverKind,
} from '@/lib/api';
import { FieldError } from './FieldError';

const INTENT_TEXT =
  'I have read the document above in full. By clicking below, I authorize my typed legal name and initials to be applied to every Signature and Initial sign field in this document, and I agree to be bound by its terms.';
const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? '';
const ADDRESS_AUTOCOMPLETE_MIN_CHARS = 4;
const ADDRESS_AUTOCOMPLETE_DEBOUNCE_MS = 250;
const SIGNATURE_FONT_FAMILY = '"SignatureScript", "Brush Script MT", "Apple Chancery", cursive';

type GooglePlacePrediction = {
  placeId?: string;
  text?: { text?: string };
};

type GoogleAutocompleteResponse = {
  suggestions?: Array<{ placePrediction?: GooglePlacePrediction }>;
};

type AddressSuggestion = {
  id: string;
  label: string;
  placeId: string;
};

type GoogleAddressComponent = {
  longText?: string;
  shortText?: string;
  types?: string[];
};

type GooglePlaceDetailsResponse = {
  addressComponents?: GoogleAddressComponent[];
};

async function fetchAddressSuggestions(
  query: string,
  signal: AbortSignal
): Promise<AddressSuggestion[]> {
  if (!GOOGLE_MAPS_API_KEY) return [];
  const response = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
    method: 'POST',
    signal,
    headers: {
      'Content-Type': 'application/json',
      'X-Goog-Api-Key': GOOGLE_MAPS_API_KEY,
      'X-Goog-FieldMask':
        'suggestions.placePrediction.placeId,suggestions.placePrediction.text.text',
    },
    body: JSON.stringify({
      input: query,
      includedRegionCodes: ['us'],
      includedPrimaryTypes: ['street_address', 'premise'],
    }),
  });
  if (!response.ok) return [];
  const data = (await response.json()) as GoogleAutocompleteResponse;
  return (data.suggestions ?? [])
    .map((item) => item.placePrediction)
    .filter(
      (
        prediction
      ): prediction is Required<Pick<GooglePlacePrediction, 'placeId'>> & GooglePlacePrediction =>
        Boolean(prediction?.placeId && prediction.text?.text)
    )
    .map((prediction) => ({
      id: prediction.placeId,
      placeId: prediction.placeId,
      label: prediction.text?.text ?? '',
    }));
}

function getGoogleAddressPart(
  components: GoogleAddressComponent[] | undefined,
  type: string,
  useShortText = false
): string {
  const part = components?.find((component) => component.types?.includes(type));
  if (!part) return '';
  return (useShortText ? part.shortText : part.longText) ?? '';
}

async function fetchGooglePlacePersonalInfo(
  current: WaiverPersonalInfo,
  suggestion: AddressSuggestion
): Promise<WaiverPersonalInfo | null> {
  if (!GOOGLE_MAPS_API_KEY) return null;
  const response = await fetch(
    `https://places.googleapis.com/v1/places/${encodeURIComponent(suggestion.placeId)}`,
    {
      headers: {
        'X-Goog-Api-Key': GOOGLE_MAPS_API_KEY,
        'X-Goog-FieldMask': 'addressComponents',
      },
    }
  );
  if (!response.ok) return null;
  const data = (await response.json()) as GooglePlaceDetailsResponse;
  const components = data.addressComponents;
  const streetNumber = getGoogleAddressPart(components, 'street_number');
  const route = getGoogleAddressPart(components, 'route', true);
  const address = [streetNumber, route].filter(Boolean).join(' ');
  const city =
    getGoogleAddressPart(components, 'locality') ||
    getGoogleAddressPart(components, 'sublocality') ||
    getGoogleAddressPart(components, 'postal_town') ||
    getGoogleAddressPart(components, 'administrative_area_level_3');
  const state = getGoogleAddressPart(components, 'administrative_area_level_1', true);
  const zip = getGoogleAddressPart(components, 'postal_code');

  return {
    ...current,
    address: address || suggestion.label.split(',')[0]?.trim() || current.address,
    city: city || current.city,
    state: state ? state.toUpperCase().slice(0, 2) : current.state,
    zip: zip || current.zip,
  };
}

// ESIGN Act / Texas UETA consumer disclosure. Stored verbatim with each signature
// so we can prove later exactly what the signer affirmed.
const ESIGN_DISCLOSURE = [
  'You are signing electronically. Your typed legal name, intent statement, IP',
  'address, and the timestamp will be recorded and combined into a tamper-evident',
  'hash. By checking the box below you (a) consent to use electronic records and',
  'electronic signatures for this transaction, (b) confirm you can read this',
  'disclosure on a device with HTML/PDF support, and (c) understand you may',
  'request a paper copy of any signed document at no cost by emailing',
  'admin@muaythaiboxing.com or asking the front desk.',
].join(' ');

const MINOR_KINDS = new Set<WaiverKind>(['LIABILITY_MINOR', 'SPARRING_MINOR']);
const LIABILITY_KINDS = new Set<WaiverKind>(['LIABILITY_ADULT', 'LIABILITY_MINOR']);

export interface SignedSet {
  [kind: string]: { signedAt: string; contentHash: string; signatureId?: string };
}

/**
 * Address + medical-history fields the liability waiver text references in its
 * "Print Name / Address / Telephone / Email / Medical Conditions / Signature"
 * block. Captured in the FE form so the signed record matches the printable
 * waiver. Backend persistence of these fields is a follow-up.
 */
export interface WaiverPersonalInfo {
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  dateOfBirth: string;
  medicalConditions: string;
}

function formatSignedAt(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      dateStyle: 'long',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

function formatSignedDate(iso: string): string {
  try {
    const d = new Date(iso);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(2);
    return `${mm}/${dd}/${yy}`;
  } catch {
    return iso;
  }
}

/** Format US numbers while preserving international numbers with country codes. */
export function formatPhoneInput(raw: string): string {
  const hasCountryCode = /^\s*\+/.test(raw);
  const digits = raw.replace(/\D/g, '').slice(0, 15);
  if (digits.length === 0) return '';
  if (hasCountryCode || digits.length > 10) return `+${digits}`;
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function formatSignError(err: unknown): string {
  const msg = String((err as Error).message ?? err);
  if (msg === 'database_unavailable') {
    return 'Signing failed: the waiver system is not connected to the database. Check the backend environment.';
  }
  if (msg === 'sign_failed') {
    return "Signing failed: we couldn't save the waiver. Please try again.";
  }
  return `Signing failed: ${msg}`;
}

export interface WaiverFieldErrors {
  legalName?: string | null;
  esign?: string | null;
  participantName?: string | null;
  waivers?: string | null;
  address?: string | null;
  dateOfBirth?: string | null;
}

export function WaiversStep(props: {
  required: WaiverKind[];
  authToken: string;
  legalName: string;
  setLegalName: (s: string) => void;
  signed: SignedSet;
  setSigned: (s: SignedSet) => void;
  signerEmail: string;
  personalInfo: WaiverPersonalInfo;
  setPersonalInfo: (info: WaiverPersonalInfo) => void;
  fieldErrors?: WaiverFieldErrors;
  clearFieldError?: (key: keyof WaiverFieldErrors) => void;
}) {
  const fieldErrors = props.fieldErrors ?? {};
  const clearError = (k: keyof WaiverFieldErrors) => props.clearFieldError?.(k);
  const [waivers, setWaivers] = useState<Waiver[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flashTargetId, setFlashTargetId] = useState<string | null>(null);
  // Texas's "express negligence" doctrine for liability releases looks at
  // whether the signer had a meaningful opportunity to review the document.
  // We require the user to scroll the waiver pre to its end before the sign
  // button enables — the read-confirmation is per-waiver and per-mount.
  const [readKinds, setReadKinds] = useState<Set<WaiverKind>>(new Set());
  const markRead = (k: WaiverKind) =>
    setReadKinds((prev) => (prev.has(k) ? prev : new Set(prev).add(k)));

  // ESIGN disclosure consent — required before any signature is accepted.
  const [esignConsent, setEsignConsent] = useState(false);
  // Bumped when fieldErrors.esign goes truthy → drives the row-flash animation.
  const [esignFlashKey, setEsignFlashKey] = useState(0);
  useEffect(() => {
    if (fieldErrors.esign) setEsignFlashKey((k) => k + 1);
  }, [fieldErrors.esign]);

  // Minor-waiver fields. Only required if any required waiver is a minor kind.
  const needsMinorFields = props.required.some((k) => MINOR_KINDS.has(k));
  const needsAddressFields = props.required.some((k) => LIABILITY_KINDS.has(k));
  const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>([]);
  const [addressSuggestionsOpen, setAddressSuggestionsOpen] = useState(false);
  const [participantName, setParticipantName] = useState('');
  const [signerRelationship, setSignerRelationship] = useState<SignerRelationship>('PARENT');

  useEffect(() => {
    let cancelled = false;
    listWaivers(props.required)
      .then((r) => {
        if (!cancelled) setWaivers(r.waivers);
      })
      .catch((e) => {
        if (!cancelled) setError(`Couldn't load waivers: ${String(e)}`);
      });
    return () => {
      cancelled = true;
    };
  }, [props.required]);

  useEffect(() => {
    if (!needsAddressFields) return;
    const query = props.personalInfo.address.trim();
    if (query.length < ADDRESS_AUTOCOMPLETE_MIN_CHARS) {
      setAddressSuggestions([]);
      return;
    }

    const controller = new AbortController();
    const timeout = window.setTimeout(() => {
      fetchAddressSuggestions(query, controller.signal)
        .then((items) => {
          setAddressSuggestions(items);
          setAddressSuggestionsOpen(items.length > 0);
        })
        .catch((e) => {
          if ((e as Error).name !== 'AbortError') setAddressSuggestions([]);
        });
    }, ADDRESS_AUTOCOMPLETE_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [needsAddressFields, props.personalInfo.address]);

  const flashClass = (id: string) => (flashTargetId === id ? ' flash-error-border' : '');

  function scrollToAndFlash(id: string, message: string): false {
    setError(message);
    setFlashTargetId(null);
    window.setTimeout(() => {
      setFlashTargetId(id);
      const el = document.getElementById(id);
      if (typeof el?.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 0);
    return false;
  }

  function waiverIsReadableNow(kind: WaiverKind): boolean {
    const el = document.getElementById(`waiver-scroll-${kind}`);
    if (!el) return false;
    return (
      el.scrollHeight <= el.clientHeight + 1 ||
      el.scrollTop + el.clientHeight >= el.scrollHeight - 8
    );
  }

  function validateBeforeSign(w: Waiver): boolean {
    if (!esignConsent) {
      return scrollToAndFlash(
        'field-esign',
        'Check the electronic signature consent box before signing.'
      );
    }
    if (props.legalName.trim().length < 2) {
      return scrollToAndFlash('field-legal-name', 'Type your legal full name before signing.');
    }
    if (needsMinorFields && participantName.trim().length < 2) {
      return scrollToAndFlash(
        'field-participant-name',
        "Type the minor participant's legal full name before signing."
      );
    }
    if (needsAddressFields) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(props.personalInfo.dateOfBirth.trim())) {
        return scrollToAndFlash('field-date-of-birth', 'Enter your date of birth before signing.');
      }
      if (props.personalInfo.address.trim().length < 3) {
        return scrollToAndFlash(
          'field-address-street',
          'Enter your street address before signing.'
        );
      }
      if (props.personalInfo.city.trim().length < 2) {
        return scrollToAndFlash('field-address-city', 'Enter your city before signing.');
      }
      if (props.personalInfo.state.trim().length < 2) {
        return scrollToAndFlash('field-address-state', 'Enter your state before signing.');
      }
      if (!/^\d{5}(-\d{4})?$/.test(props.personalInfo.zip.trim())) {
        return scrollToAndFlash('field-address-zip', 'Enter a valid ZIP code before signing.');
      }
      if (props.personalInfo.phone.replace(/\D/g, '').length < 7) {
        return scrollToAndFlash('field-address-phone', 'Enter your phone number before signing.');
      }
    }
    if (!readKinds.has(w.kind) && !waiverIsReadableNow(w.kind)) {
      return scrollToAndFlash(
        `field-waiver-read-${w.kind}`,
        'Scroll to the end of the waiver document before signing.'
      );
    }
    if (!readKinds.has(w.kind)) markRead(w.kind);
    return true;
  }

  function updatePersonalInfo<K extends keyof WaiverPersonalInfo>(
    key: K,
    value: WaiverPersonalInfo[K]
  ): void {
    const next = { ...props.personalInfo, [key]: value };
    props.setPersonalInfo(next);
    // Recompute readiness against the new value so the error clears immediately.
    const ready =
      !needsAddressFields ||
      (/^\d{4}-\d{2}-\d{2}$/.test(next.dateOfBirth.trim()) &&
        next.address.trim().length >= 3 &&
        next.city.trim().length >= 2 &&
        next.state.trim().length >= 2 &&
        /^\d{5}(-\d{4})?$/.test(next.zip.trim()) &&
        next.phone.replace(/\D/g, '').length >= 7);
    if (ready) clearError('address');
  }

  async function applyAddressSuggestion(suggestion: AddressSuggestion): Promise<void> {
    setAddressSuggestionsOpen(false);
    setAddressSuggestions([]);
    const next = await fetchGooglePlacePersonalInfo(props.personalInfo, suggestion);
    if (!next) return;
    props.setPersonalInfo(next);
    const ready =
      !needsAddressFields ||
      (/^\d{4}-\d{2}-\d{2}$/.test(next.dateOfBirth.trim()) &&
        next.address.trim().length >= 3 &&
        next.city.trim().length >= 2 &&
        next.state.trim().length >= 2 &&
        /^\d{5}(-\d{4})?$/.test(next.zip.trim()) &&
        next.phone.replace(/\D/g, '').length >= 7);
    if (ready) clearError('address');
  }

  async function signOne(w: Waiver) {
    setError(null);
    if (!validateBeforeSign(w)) return;
    setBusy(true);
    try {
      const isMinor = MINOR_KINDS.has(w.kind);
      const r = await signWaiver({
        authToken: props.authToken,
        kind: w.kind,
        version: w.version,
        typedSignature: props.legalName,
        intent: INTENT_TEXT,
        esignConsent: true,
        esignDisclosure: ESIGN_DISCLOSURE,
        participantName: isMinor ? participantName.trim() : undefined,
        signerRelationship: isMinor ? signerRelationship : undefined,
        personalInfo: props.personalInfo,
      });
      const next = { ...props.signed, [w.kind]: r };
      props.setSigned(next);
      const stillMissing = props.required.some((k) => !next[k]);
      if (!stillMissing) {
        clearError('waivers');
      }
    } catch (e) {
      setError(formatSignError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid gap-4">
      <section
        id="field-esign"
        tabIndex={-1}
        className={`rounded-lg border border-bone/15 bg-neutral-900/40 p-3${flashClass('field-esign')}`}
      >
        <p className="text-sm text-bone/80">{ESIGN_DISCLOSURE}</p>
        <label
          // The flash animation lives on this row so the user's eye is drawn
          // to the missing checkbox specifically. `key` re-mounts the element
          // so consecutive failures restart the keyframes.
          key={`esign-row-${esignFlashKey}`}
          className={`mt-3 flex items-start gap-2 rounded-md p-2 text-sm text-bone/80 ${
            fieldErrors.esign && esignFlashKey > 0 ? 'flash-error-row' : ''
          }`}
          data-testid="esign-consent-row"
        >
          <input
            type="checkbox"
            checked={esignConsent}
            onChange={(e) => {
              setEsignConsent(e.target.checked);
              if (e.target.checked) clearError('esign');
            }}
            className="mt-1"
            data-testid="esign-consent"
          />
          <span>
            I consent to electronic records and electronic signatures for this transaction.
          </span>
        </label>
        <FieldError message={fieldErrors.esign} />
      </section>

      <label className="block">
        <span className="mb-1 block text-xs uppercase tracking-wider text-bone/60">
          {needsMinorFields
            ? "Parent/Guardian's legal full name (used as your signature)"
            : 'Legal full name (used as your signature)'}
        </span>
        <input
          id="field-legal-name"
          type="text"
          required
          value={props.legalName}
          onChange={(e) => {
            props.setLegalName(e.target.value);
            if (e.target.value.length >= 2) clearError('legalName');
          }}
          className={`input${flashClass('field-legal-name')}`}
          autoComplete="name"
        />
        <FieldError message={fieldErrors.legalName} />
      </label>

      {needsMinorFields && (
        <div className="grid gap-3 rounded-lg border border-bone/15 bg-neutral-900/40 p-3">
          <p className="text-xs uppercase tracking-wider text-accent">Minor participant</p>
          <label className="block">
            <span className="mb-1 block text-xs uppercase tracking-wider text-bone/60">
              Minor's legal full name
            </span>
            <input
              id="field-participant-name"
              type="text"
              required
              value={participantName}
              onChange={(e) => {
                setParticipantName(e.target.value);
                if (e.target.value.trim().length >= 2) clearError('participantName');
              }}
              className={`input${flashClass('field-participant-name')}`}
              data-testid="participant-name"
            />
            <FieldError message={fieldErrors.participantName} />
          </label>
          <fieldset>
            <legend className="mb-1 block text-xs uppercase tracking-wider text-bone/60">
              Your relationship to the minor
            </legend>
            <div className="flex gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="signerRelationship"
                  value="PARENT"
                  checked={signerRelationship === 'PARENT'}
                  onChange={() => setSignerRelationship('PARENT')}
                />
                Parent
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="signerRelationship"
                  value="GUARDIAN"
                  checked={signerRelationship === 'GUARDIAN'}
                  onChange={() => setSignerRelationship('GUARDIAN')}
                />
                Legal guardian
              </label>
            </div>
          </fieldset>
        </div>
      )}

      {needsAddressFields && (
        <div id="field-address" tabIndex={-1} className="grid gap-3">
          <label className="block">
            <span className="mb-1 block text-xs uppercase tracking-wider text-bone/60">
              Date of birth
            </span>
            <input
              id="field-date-of-birth"
              type="date"
              required
              value={props.personalInfo.dateOfBirth}
              onChange={(e) => updatePersonalInfo('dateOfBirth', e.target.value)}
              className={`input${flashClass('field-date-of-birth')}`}
              data-testid="waiver-date-of-birth"
            />
            <FieldError message={fieldErrors.dateOfBirth} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs uppercase tracking-wider text-bone/60">
              Street address
            </span>
            <div className="relative">
              <input
                id="field-address-street"
                type="text"
                required
                value={props.personalInfo.address}
                onChange={(e) => {
                  updatePersonalInfo('address', e.target.value);
                  setAddressSuggestionsOpen(true);
                }}
                onFocus={() => {
                  if (addressSuggestions.length > 0) setAddressSuggestionsOpen(true);
                }}
                onBlur={() => {
                  window.setTimeout(() => setAddressSuggestionsOpen(false), 120);
                }}
                className={`input${flashClass('field-address-street')}`}
                autoComplete="street-address"
                aria-autocomplete="list"
                aria-controls="waiver-address-suggestions"
                aria-expanded={addressSuggestionsOpen}
                data-testid="waiver-address"
              />
              {addressSuggestionsOpen && addressSuggestions.length > 0 && (
                <ul
                  id="waiver-address-suggestions"
                  role="listbox"
                  className="absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-md border border-bone/15 bg-neutral-950 py-1 text-sm text-bone shadow-xl"
                  data-testid="address-suggestions"
                >
                  {addressSuggestions.map((suggestion, idx) => (
                    <li key={suggestion.id} role="option" aria-selected={false}>
                      <button
                        type="button"
                        className="block w-full px-3 py-2 text-left hover:bg-bone/10 focus:bg-bone/10"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => void applyAddressSuggestion(suggestion)}
                        data-testid={`address-suggestion-${idx}`}
                      >
                        {suggestion.label}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </label>
          <div className="grid gap-3 sm:grid-cols-[1fr_8rem_8rem]">
            <label className="block">
              <span className="mb-1 block text-xs uppercase tracking-wider text-bone/60">City</span>
              <input
                id="field-address-city"
                type="text"
                required
                value={props.personalInfo.city}
                onChange={(e) => updatePersonalInfo('city', e.target.value)}
                className={`input${flashClass('field-address-city')}`}
                autoComplete="address-level2"
                placeholder="Irving"
                data-testid="waiver-city"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs uppercase tracking-wider text-bone/60">
                State
              </span>
              <input
                id="field-address-state"
                type="text"
                required
                maxLength={2}
                value={props.personalInfo.state}
                onChange={(e) =>
                  updatePersonalInfo('state', e.target.value.toUpperCase().slice(0, 2))
                }
                className={`input uppercase${flashClass('field-address-state')}`}
                autoComplete="address-level1"
                placeholder="TX"
                data-testid="waiver-state"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs uppercase tracking-wider text-bone/60">
                Zip code
              </span>
              <input
                id="field-address-zip"
                type="text"
                required
                inputMode="numeric"
                value={props.personalInfo.zip}
                onChange={(e) => updatePersonalInfo('zip', e.target.value)}
                className={`input${flashClass('field-address-zip')}`}
                autoComplete="postal-code"
                placeholder="75063"
                data-testid="waiver-zip"
              />
            </label>
          </div>
          <label className="block">
            <span className="mb-1 block text-xs uppercase tracking-wider text-bone/60">
              Telephone
            </span>
            <input
              id="field-address-phone"
              type="tel"
              required
              value={props.personalInfo.phone}
              onChange={(e) => updatePersonalInfo('phone', formatPhoneInput(e.target.value))}
              className={`input${flashClass('field-address-phone')}`}
              autoComplete="tel"
              placeholder="(972) 555-0123 or +44 20 7123 4567"
              data-testid="waiver-phone"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs uppercase tracking-wider text-bone/60">
              Medical conditions
            </span>
            <textarea
              rows={2}
              value={props.personalInfo.medicalConditions}
              onChange={(e) => updatePersonalInfo('medicalConditions', e.target.value)}
              className="input min-h-[3rem]"
              data-testid="waiver-medical"
            />
          </label>
          <FieldError message={fieldErrors.address} />
        </div>
      )}

      <div id="field-waivers" tabIndex={-1} className="grid gap-4">
        {waivers.map((w) => {
          const isSigned = Boolean(props.signed[w.kind]);
          const sig = props.signed[w.kind];
          return (
            <article
              id={`field-waiver-read-${w.kind}`}
              key={w.kind}
              className={`overflow-hidden rounded-lg border border-neutral-300 bg-white text-neutral-900 shadow-sm${flashClass(`field-waiver-read-${w.kind}`)}`}
              data-testid={`waiver-${w.kind}`}
            >
              <header className="flex items-center justify-between gap-3 border-b border-neutral-300 bg-neutral-200 px-4 py-3">
                <h4 className="font-display text-base text-neutral-900">{w.title}</h4>
                {isSigned ? (
                  <span className="text-xs uppercase tracking-wider text-emerald-700">Signed</span>
                ) : (
                  <span className="text-xs uppercase tracking-wider text-neutral-600">
                    Required
                  </span>
                )}
              </header>
              <WaiverBody
                text={w.text}
                kind={w.kind}
                onReadEnd={markRead}
                signed={isSigned}
                legalName={props.legalName}
                signerEmail={props.signerEmail}
                participantName={participantName}
                signerRelationship={signerRelationship}
                personalInfo={props.personalInfo}
                signedAt={sig?.signedAt ?? null}
                signatureId={sig?.signatureId}
                contentHash={sig?.contentHash}
              />

              {!isSigned && (
                <div className="border-t border-neutral-300 bg-neutral-100 p-3">
                  {!readKinds.has(w.kind) && <ScrollWarning kind={w.kind} />}
                  <p
                    className={
                      readKinds.has(w.kind)
                        ? 'text-xs text-neutral-700'
                        : 'mt-3 text-xs text-neutral-700'
                    }
                  >
                    {INTENT_TEXT}
                  </p>
                  <button
                    type="button"
                    onClick={() => signOne(w)}
                    disabled={busy}
                    className="btn-primary mt-3 disabled:opacity-50"
                  >
                    {busy ? 'Signing…' : `I agree — sign ${w.title}`}
                  </button>
                </div>
              )}
            </article>
          );
        })}
        <FieldError message={fieldErrors.waivers} />
      </div>

      {error && (
        <div
          role="alert"
          className="rounded border border-accent/50 bg-accent/10 p-3 text-sm text-accent"
        >
          {error}
        </div>
      )}
    </div>
  );
}

function WaiverBody({
  text,
  kind,
  onReadEnd,
  signed,
  legalName,
  signerEmail,
  participantName,
  signerRelationship,
  personalInfo,
  signedAt,
  signatureId,
  contentHash,
}: {
  text: string;
  kind: WaiverKind;
  onReadEnd: (k: WaiverKind) => void;
  signed: boolean;
  legalName: string;
  signerEmail: string;
  participantName: string;
  signerRelationship: SignerRelationship;
  personalInfo: WaiverPersonalInfo;
  signedAt: string | null;
  signatureId?: string;
  contentHash?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);

  // Document shorter than the viewport → nothing to scroll, mark read on mount.
  // Otherwise, listen for the scroll position to reach the end (within 8px).
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => {
      const reachedEnd =
        el.scrollHeight <= el.clientHeight + 1 ||
        el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
      if (reachedEnd) onReadEnd(kind);
    };
    check();
    el.addEventListener('scroll', check, { passive: true });
    return () => el.removeEventListener('scroll', check);
  }, [kind, onReadEnd]);

  const displayText = signed
    ? fillWaiverFields(text, {
        legalName,
        email: signerEmail,
        participantName,
        signerRelationship,
        address: personalInfo.address,
        city: personalInfo.city,
        state: personalInfo.state,
        zip: personalInfo.zip,
        phone: personalInfo.phone,
        dateOfBirth: personalInfo.dateOfBirth,
        medicalConditions: personalInfo.medicalConditions,
        signedDate: signedAt ? formatSignedDate(signedAt) : '',
      })
    : text;

  return (
    <>
      <div
        id={`waiver-scroll-${kind}`}
        ref={ref}
        className="max-h-96 overflow-auto bg-white p-4 text-sm leading-7 text-neutral-900"
        data-testid={`waiver-body-${kind}`}
      >
        <div className="mx-auto max-w-3xl">
          {renderWaiverLines(displayText, kind, legalName, signed)}
        </div>
      </div>
      {signed && signedAt && (
        <p
          className="border-t border-neutral-300 bg-white px-4 py-2 text-[11px] leading-relaxed text-neutral-500"
          data-testid={`signature-meta-${kind}`}
        >
          Electronically signed by <span className="text-neutral-800">{legalName}</span> (
          {signerEmail}) on {formatSignedAt(signedAt)}
          {signatureId ? <> · ID {signatureId.slice(0, 12)}</> : null}
          {contentHash ? <> · Hash {contentHash.slice(0, 16)}…</> : null}
        </p>
      )}
    </>
  );
}

/**
 * Replace the labeled blanks in the printable waiver form with the captured
 * field values. The waiver text is treated as a printable form: each label
 * (Email, Address, …) sits at the start of its own line and is followed by
 * blank lines on the printed sheet. Digitally we splice the value onto the
 * label line itself, after the colon.
 */
function fillWaiverFields(
  text: string,
  fields: {
    legalName: string;
    email: string;
    participantName: string;
    signerRelationship: SignerRelationship;
    address: string;
    city: string;
    state: string;
    zip: string;
    phone: string;
    dateOfBirth: string;
    medicalConditions: string;
    signedDate: string;
  }
): string {
  const safe = (v: string) => v.replace(/[\r\n]/g, ' ').trim();
  const nameParts = (name: string) => {
    const parts = safe(name).split(/\s+/).filter(Boolean);
    return { first: parts[0] ?? '', last: parts.slice(1).join(' ') };
  };
  const initialsRaw = safe(fields.legalName)
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase())
    .filter(Boolean)
    .join('.');
  const initials = initialsRaw ? `${initialsRaw}.` : '';
  const signerRelationshipLabel = fields.signerRelationship === 'PARENT' ? 'Parent' : 'Guardian';

  let participantSection = false;
  const nameFilled = text
    .split('\n')
    .map((line) => {
      if (/Minor Participant/i.test(line)) participantSection = true;
      const match = line.match(/^(\s*)First Name:?\s+Last Name:?\s*$/);
      if (!match) return line;
      const name =
        participantSection && fields.participantName ? fields.participantName : fields.legalName;
      const { first, last } = nameParts(name);
      participantSection = false;
      return `${match[1]}First Name: ${first}      Last Name: ${last}`;
    })
    .join('\n');

  let out = nameFilled;
  out = out.replace(/^(\s*)Initial sign:\s*$/gm, `$1Initial sign: ${initials}`);
  out = out.replace(/^(\s*)Address:\s*$/m, `$1Address: ${safe(fields.address)}`);
  out = out.replace(/^(\s*)Zip Code:\s*$/m, `$1Zip Code: ${safe(fields.zip)}`);
  out = out.replace(/^(\s*)Email:\s*$/m, `$1Email: ${safe(fields.email)}`);
  out = out.replace(/^(\s*)Telephone:\s*$/m, `$1Telephone: ${safe(fields.phone)}`);
  out = out.replace(/^(\s*)Phone:\s*$/m, `$1Phone: ${safe(fields.phone)}`);
  out = out.replace(/^(\s*)Phone Number:\s*$/m, `$1Phone Number: ${safe(fields.phone)}`);
  out = out.replace(/^(\s*)Date of birth:\s*$/m, `$1Date of birth: ${safe(fields.dateOfBirth)}`);
  out = out.replace(
    /^(\s*)Medical Conditions:\s*$/m,
    `$1Medical Conditions: ${safe(fields.medicalConditions) || 'None reported'}`
  );
  // City: ___ State: ___ on one line — keep both halves filled in-place.
  out = out.replace(
    /^(\s*)City:(\s+)State:\s*$/m,
    `$1City: ${safe(fields.city)}$2State: ${safe(fields.state)}`
  );
  // Phone: ___ Email: ___ on one line (minor liability waiver layout).
  out = out.replace(
    /^(\s*)Phone:(\s+)Email:\s*$/m,
    `$1Phone: ${safe(fields.phone)}$2Email: ${safe(fields.email)}`
  );
  out = out.replace(
    /^(\s*)Relationship to Minor:\s*$/m,
    `$1Relationship to Minor: ${signerRelationshipLabel}`
  );
  // Signature ___ Date (mm/dd/yy) — fill both columns with the typed signature
  // (just the name, prefixed with "Signature ") and the date the backend
  // stamped on the signature record.
  out = out.replace(
    /^(\s*)Signature(\s+)Date \(mm\/dd\/yy\)\s*$/m,
    `$1Signature ${safe(fields.legalName)}$2Date ${safe(fields.signedDate)}`
  );
  // Older sparring waivers use "Date" without the (mm/dd/yy) suffix.
  out = out.replace(
    /^(\s*)Signature(\s+)Date\s*$/m,
    `$1Signature ${safe(fields.legalName)}$2Date ${safe(fields.signedDate)}`
  );
  return out;
}

/**
 * Render the waiver text post-substitution with two visual cues:
 * 1. Substituted values appear in a distinct color so the user can tell at a
 *    glance what was injected by the system vs the static contract text.
 * 2. The "Signature ___ Date" line specifically renders the signer's name in
 *    a cursive script font (the only place we override the monospace pre).
 *
 * Operates on lines that match the same patterns the substitution helper
 * rewrote — anything else falls through as plain monospace text.
 */
function isWaiverTitleLine(line: string): boolean {
  const trimmed = line.trim();
  return trimmed.length >= 5 && trimmed.length <= 100 && /[A-Za-z]/.test(trimmed);
}

function isWaiverNoticeLine(line: string): boolean {
  const trimmed = line.trim();
  return (
    trimmed.length >= 20 &&
    /[A-Z]/.test(trimmed) &&
    trimmed === trimmed.toUpperCase() &&
    !/^\d+\./.test(trimmed)
  );
}

function normalizeWaiverRenderLine(line: string): string {
  return line
    .replace(/\f+/g, '')
    .replace(/^\s+/, '')
    .replace(/[ \t]+$/, '');
}

function isFillableWaiverLine(line: string): boolean {
  return (
    /^(Initial sign|Address|Zip Code|Email|Telephone|Phone|Phone Number|Date of birth|Medical Conditions|Relationship to Minor):\s*$/i.test(
      line
    ) ||
    /^First Name:?\s+Last Name:?\s*$/i.test(line) ||
    /^City:\s+State:\s*$/i.test(line) ||
    /^Phone:\s+Email:\s*$/i.test(line) ||
    /^Signature\s+Date(?: \(mm\/dd\/yy\))?\s*$/i.test(line)
  );
}

function isFilledWaiverFieldLine(line: string): boolean {
  return (
    /^(Address|Zip Code|Email|Telephone|Phone|Phone Number|Date of birth|Medical Conditions|Relationship to Minor):\s+.+/i.test(
      line
    ) ||
    /^First Name:\s*.*\S\s+Last Name:\s*.*$/i.test(line) ||
    /^City:\s*.*\S\s+State:\s*.*\S\s*$/i.test(line) ||
    /^Phone:\s*.*\S\s+Email:\s*.*\S\s*$/i.test(line) ||
    /^Signature\s+.+\s+Date\s+\d{2}\/\d{2}\/\d{2,4}\s*$/i.test(line) ||
    /^Initial sign:\s+.+/i.test(line)
  );
}

function shouldKeepWaiverLineBreak(line: string): boolean {
  const trimmed = line.trim();
  return (
    trimmed === '' ||
    lineHasPageBreak(line) ||
    isFillableWaiverLine(trimmed) ||
    isFilledWaiverFieldLine(trimmed) ||
    /^(Print Name|Parent\/Guardian Information|I have read, understood)/i.test(trimmed) ||
    (trimmed.length >= 5 &&
      trimmed.length <= 100 &&
      trimmed === trimmed.toUpperCase() &&
      /[A-Z]/.test(trimmed))
  );
}

function normalizeWaiverTextForReading(text: string): string {
  const lines = text.replace(/\f/g, '\n\f\n').split('\n');
  const out: string[] = [];
  let paragraph: string[] = [];

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    out.push(paragraph.join(' ').replace(/\s+/g, ' ').trim());
    paragraph = [];
  };

  for (const rawLine of lines) {
    if (rawLine.includes('\f')) {
      flushParagraph();
      out.push('\f');
      continue;
    }
    const line = normalizeWaiverRenderLine(rawLine);
    if (line === '') {
      flushParagraph();
      out.push('');
      continue;
    }
    if (shouldKeepWaiverLineBreak(line)) {
      flushParagraph();
      out.push(line);
      continue;
    }
    paragraph.push(line.trim());
  }

  flushParagraph();
  return out.join('\n');
}

function lineHasPageBreak(line: string): boolean {
  return line.includes('\f');
}

function waiverLineClass(line: string, firstTextLine: number, idx: number): string {
  if (/^\d+\./.test(line)) {
    return idx === firstTextLine
      ? 'mt-0 block break-inside-avoid'
      : 'mt-4 block break-inside-avoid';
  }
  if (/^(Print Name|Parent\/Guardian Information|I have read, understood)/i.test(line)) {
    return 'mt-5 block break-inside-avoid font-semibold';
  }
  return 'block';
}

function previousNonEmptyWaiverLine(lines: string[], idx: number): string {
  for (let i = idx - 1; i >= 0; i -= 1) {
    const line = normalizeWaiverRenderLine(lines[i]);
    if (line !== '') return line;
  }
  return '';
}

function renderWaiverLines(
  text: string,
  kind: WaiverKind,
  legalName: string,
  highlightValues: boolean
): ReactNode {
  const injected = 'text-emerald-700 font-semibold';
  const cursive: CSSProperties = {
    display: 'inline-block',
    fontFamily: SIGNATURE_FONT_FAMILY,
    fontSize: '1.9em',
    lineHeight: 1,
    fontWeight: 400,
    marginLeft: '4px',
  };
  const initialCursive: CSSProperties = {
    ...cursive,
    fontSize: '1.7em',
  };
  const lines = normalizeWaiverTextForReading(text).split('\n');
  const firstTextLine = lines.findIndex((line) => line.trim() !== '');
  return lines.map((line, idx) => {
    const displayLine = normalizeWaiverRenderLine(line);
    const pageBreak = lineHasPageBreak(line) ? (
      <span
        key={`${idx}-page-break`}
        aria-hidden="true"
        className="my-6 block border-t border-neutral-200 pt-6"
      />
    ) : null;

    if (displayLine === '') {
      return pageBreak;
    }

    if (!highlightValues && isFillableWaiverLine(displayLine)) {
      if (isFillableWaiverLine(previousNonEmptyWaiverLine(lines, idx))) return null;
      return (
        <Fragment key={idx}>
          {pageBreak}
          <span className="my-2 block break-inside-avoid text-xs italic leading-5 text-neutral-500">
            [to fill click the button below]
          </span>
        </Fragment>
      );
    }

    if (idx === firstTextLine && isWaiverTitleLine(displayLine)) {
      return (
        <Fragment key={idx}>
          {pageBreak}
          <span className="mb-4 block break-inside-avoid text-center text-xl font-bold leading-tight text-neutral-950 sm:text-2xl">
            {displayLine.trim()}
          </span>
        </Fragment>
      );
    }
    if (idx > firstTextLine && idx < firstTextLine + 4 && isWaiverNoticeLine(displayLine)) {
      if (isWaiverNoticeLine(normalizeWaiverRenderLine(lines[idx - 1] ?? ''))) return null;
      const notice = [];
      for (
        let i = idx;
        i < lines.length && isWaiverNoticeLine(normalizeWaiverRenderLine(lines[i]));
        i += 1
      ) {
        notice.push(normalizeWaiverRenderLine(lines[i]).trim());
      }
      return (
        <Fragment key={idx}>
          {pageBreak}
          <span className="mx-auto mb-5 block max-w-3xl break-inside-avoid text-center text-sm font-semibold leading-6 tracking-wide text-neutral-800 sm:text-base">
            {notice.join(' ')}
          </span>
        </Fragment>
      );
    }

    // "    Signature <Name>      Date <mm/dd/yy>"
    const sigDate = displayLine.match(
      /^(\s*)Signature\s+(\S(?:.*\S)?)(\s+)Date\s+(\d{2}\/\d{2}\/\d{2,4})\s*$/
    );
    if (highlightValues && sigDate) {
      const [, indent, signedName, gap, date] = sigDate;
      return (
        <Fragment key={idx}>
          {pageBreak}
          <span className="mt-4 block break-inside-avoid">
            {indent}Signature{' '}
            <span
              data-testid={`signature-cursive-${kind}`}
              style={cursive}
              className="text-neutral-900"
            >
              {signedName || legalName}
            </span>
            {gap}Date <span className={injected}>{date}</span>
          </span>
        </Fragment>
      );
    }

    const initialSign = displayLine.match(/^(\s*)Initial sign:\s*(.+?)\s*$/);
    if (highlightValues && initialSign) {
      return (
        <Fragment key={idx}>
          {pageBreak}
          <span className="mb-4 mt-2 block break-inside-avoid">
            {initialSign[1]}Initial sign:{' '}
            <span
              data-testid={`initial-signature-${kind}-${idx}`}
              style={initialCursive}
              className="text-neutral-900"
            >
              {initialSign[2]}
            </span>
          </span>
        </Fragment>
      );
    }

    // "    City: <city>      State: <state>"
    const cityState = displayLine.match(/^(\s*)City:\s*(.*\S)(\s{2,})State:\s*(.*\S)\s*$/);
    if (highlightValues && cityState) {
      return (
        <Fragment key={idx}>
          {pageBreak}
          <span className="mt-1 block break-inside-avoid">
            {cityState[1]}City: <span className={injected}>{cityState[2]}</span>
            {cityState[3]}State: <span className={injected}>{cityState[4]}</span>
          </span>
        </Fragment>
      );
    }

    // "    Phone: <phone>      Email: <email>" (minor liability layout).
    const phoneEmail = displayLine.match(/^(\s*)Phone:\s*(.*\S)(\s{2,})Email:\s*(.*\S)\s*$/);
    if (highlightValues && phoneEmail) {
      return (
        <Fragment key={idx}>
          {pageBreak}
          <span className="mt-1 block break-inside-avoid">
            {phoneEmail[1]}Phone: <span className={injected}>{phoneEmail[2]}</span>
            {phoneEmail[3]}Email: <span className={injected}>{phoneEmail[4]}</span>
          </span>
        </Fragment>
      );
    }

    // "    First Name: <first>      Last Name: <last>"
    const firstLast = displayLine.match(
      /^(\s*)First Name:\s*(.*?\S?)(\s+)Last Name:\s*(.*\S?)\s*$/
    );
    if (highlightValues && firstLast) {
      return (
        <Fragment key={idx}>
          {pageBreak}
          <span className="mt-1 block break-inside-avoid">
            {firstLast[1]}First Name: <span className={injected}>{firstLast[2]}</span>
            {firstLast[3]}Last Name: <span className={injected}>{firstLast[4]}</span>
          </span>
        </Fragment>
      );
    }

    // "    Label: <value>" — single-field line.
    const labeled = displayLine.match(
      /^(\s*)(Address|Zip Code|Telephone|Phone|Phone Number|Email|Medical Conditions|Date of birth|Relationship to Minor):\s+(.+?)\s*$/
    );
    if (highlightValues && labeled) {
      return (
        <Fragment key={idx}>
          {pageBreak}
          <span className="mt-1 block break-inside-avoid">
            {labeled[1]}
            {labeled[2]}: <span className={injected}>{labeled[3]}</span>
          </span>
        </Fragment>
      );
    }

    return (
      <Fragment key={idx}>
        {pageBreak}
        <span className={waiverLineClass(displayLine, firstTextLine, idx)}>{displayLine}</span>
      </Fragment>
    );
  });
}

function ScrollWarning({ kind }: { kind: WaiverKind }) {
  return (
    <div
      role="status"
      className="flex items-start gap-2 rounded-md border-2 border-yellow-700 bg-yellow-200 p-3 text-sm font-medium text-yellow-950"
      data-testid={`waiver-scroll-hint-${kind}`}
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        className="mt-0.5 h-5 w-5 shrink-0 text-yellow-800"
        aria-hidden="true"
      >
        <path
          d="M12 3 1.5 21h21L12 3z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
        />
        <path d="M12 10v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <circle cx="12" cy="18" r="1" fill="currentColor" />
      </svg>
      <span>Scroll to the end of the document above to enable signing.</span>
    </div>
  );
}
